import api from '../../api/client';
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Table,
  Modal,
  Form,
  Button,
  Select,
  Input,
  Space,
  message,
  Card,
  Row,
  Col,
  DatePicker,
  Popconfirm,
  Typography,
  Upload,
  Statistic,
  Divider,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  ReloadOutlined,
  UploadOutlined,
  InboxOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { usePermissions } from "../../permissions";
import { toast } from "react-toastify";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { useMemo } from "react";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Paragraph } = Typography;
const { Dragger } = Upload;

const accountTypes = ["BALANCE_SHEET", "INCOME_STATEMENT"];
const accountNormal = ["Debit", "Credit"];
const accountCategories = [
  "CURRENT_ASSETS",
  "OTHER_ASSETS",
  "CURRENT_LIABILITIES",
  "LONG_TERM_LIABILITIES",
  "EQUITY",
  "EXPENSE",
  "LONG_TERM_ASSETS",
  "REVENUE",
  "COST_OF_SALES",
  "OPERATING_EXPENSE",
  "CONTROL_ACCOUNTS",
  "OTHER_INCOME",
];

const ChartOfAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [form] = Form.useForm();
  const [dateRange, setDateRange] = useState([null, null]);
  const [mostUsedAccounts, setMostUsedAccounts] = useState([]);
  const { createPermission, editPermission, deletePermission } =
    usePermissions();
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [previewData, setPreviewData] = useState({ columns: [], rows: [] });
  const [parsedFile, setParsedFile] = useState(null);

  const tableRef = useRef();

  // Parse uploaded file and show preview
  const parseFileForPreview = (file) => {
    const fileType = file.type;
    if (fileType === "text/csv" || file.name.endsWith(".csv")) {
      Papa.parse(file, {
        header: true,
        preview: 5, // only first 5 rows for preview
        complete: (results) => {
          const columns = results.meta.fields || [];
          const rows = results.data;
          setPreviewData({ columns, rows });
          setParsedFile(file);
        },
        error: (err) => {
          message.error("Failed to parse CSV: " + err.message);
        },
      });
    } else if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);
        if (jsonData.length > 0) {
          const columns = Object.keys(jsonData[0]);
          const rows = jsonData.slice(0, 5);
          setPreviewData({ columns, rows });
          setParsedFile(file);
        } else {
          message.warning("File contains no data");
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      message.error("Unsupported file type. Please upload CSV or Excel.");
    }
  };

  // Download template CSV
  const downloadTemplate = () => {
    const headers = [
      "Account Name",
      "Code",
      "Financial Statement",
      "Group",
      "Normal Balance",
    ];
    const exampleRow = [
      "Petty Cash",
      "1010",
      "Balance Sheet",
      "Current Assets",
      "Debit",
    ];
    const csvContent = [headers, exampleRow]
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    saveAs(blob, "chart_of_accounts_template.csv");
  };

  // Fetch chart of accounts
  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/chart-of-accounts`);
      setAccounts(data);
    } catch (error) {
      message.error("Failed to fetch accounts");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Fetch most used accounts based on date range
  useEffect(() => {
    const start = dateRange?.[0]
      ? dateRange[0].format("YYYY-MM-DD")
      : "1970-01-01";
    const end = dateRange?.[1]
      ? dateRange[1].format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");
    api
      .get(`/api/most-used-account?start=${start}&end=${end}`)
      .then((res) => setMostUsedAccounts(res.data))
      .catch((err) => console.error("Error fetching most used accounts:", err));
  }, [dateRange, api]);

  // Filter accounts based on search
  const filteredAccounts = accounts.filter((item) =>
    Object.values(item).some(
      (value) =>
        typeof value === "string" &&
        value.toLowerCase().includes(searchText.toLowerCase()),
    ),
  );

  const stats = useMemo(() => {
    const total = filteredAccounts.length;
    const balanceSheetCount = filteredAccounts.filter(
      (acc) => acc.type === "BALANCE_SHEET",
    ).length;
    const incomeStatementCount = filteredAccounts.filter(
      (acc) => acc.type === "INCOME_STATEMENT",
    ).length;

    // Count by category
    const categoryCount = {};
    filteredAccounts.forEach((acc) => {
      const cat = acc.category;
      if (cat) categoryCount[cat] = (categoryCount[cat] || 0) + 1;
    });
    const topCategories = Object.entries(categoryCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    return { total, balanceSheetCount, incomeStatementCount, topCategories };
  }, [filteredAccounts]);

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      "Account Name",
      "Account Code",
      "Type",
      "Category",
      "Normal Balance",
      "Created At",
    ];
    const rows = filteredAccounts.map((acc) => [
      acc.account_name,
      acc.gl_code,
      acc.type,
      acc.category,
      acc.normal_balance,
      dayjs(acc.created_at).format("YYYY-MM-DD"),
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    saveAs(blob, `chart_of_accounts_${dayjs().format("YYYYMMDD_HHmmss")}.csv`);
  };

  // Export to PDF (using html2canvas on the table)
  const exportToPDF = async () => {
    const tableElement = document.querySelector(".ant-table-wrapper table");
    if (!tableElement) return;
    try {
      const canvas = await html2canvas(tableElement);
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF();
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`chart_of_accounts_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`);
    } catch (error) {
      message.error("Failed to generate PDF");
    }
  };

  // Open create modal
  const handleCreate = () => {
    if (!createPermission) {
      message.warning("You don't have permission to create accounts");
      return;
    }
    setEditingAccount(null);
    form.resetFields();
    setModalVisible(true);
  };

  // Open edit modal
  const handleEdit = (record) => {
    if (!editPermission) {
      message.warning("You don't have permission to edit accounts");
      return;
    }
    setEditingAccount(record);
    form.setFieldsValue({
      account_name: record.account_name,
      type: record.type,
      category: record.category,
      normal_balance: record.normal_balance,
      gl_code: record.gl_code,
    });
    setModalVisible(true);
  };

  // Archive (delete) account
  const handleArchive = async (id) => {
    if (!deletePermission) {
      message.warning("You don't have permission to delete accounts");
      return;
    }
    try {
      const { data } = await api.put(`/api/accounts/${id}/archive`);
      toast.success(data.message || "Account archived successfully");
      fetchAccounts();
    } catch (error) {
      message.error(error.response?.data?.error || "Archive failed");
    }
  };

  // Submit create/update
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingAccount) {
        // Update
        const { data } = await api.put(
          `/accounts/update/${editingAccount.id}`,
          values,
        );
        toast.success(data.message || "Account updated successfully");
      } else {
        // Create
        const { data } = await api.post(`/accounts`, values);
        toast.success(data.message || "Account created successfully");
      }
      setModalVisible(false);
      fetchAccounts();
    } catch (error) {
      message.error(error.response?.data?.error || "Operation failed");
    }
  };

  // Handle file upload for import
  const handleImport = async () => {
    if (fileList.length === 0) {
      message.warning("Please select a file to upload");
      return;
    }
    const formData = new FormData();
    formData.append("file", fileList[0].originFileObj);

    setUploading(true);
    try {
      const { data } = await api.post(
        `/api/accounts/import`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      message.success(
        data.message || `${data.count || 0} accounts imported successfully`,
      );
      setImportModalVisible(false);
      setFileList([]);
      fetchAccounts();
    } catch (error) {
      message.error(error.response?.data?.error || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  // Table columns
  const columns = [
    {
      title: "Account Name",
      dataIndex: "account_name",
      key: "account_name",
      sorter: (a, b) => a.account_name.localeCompare(b.account_name),
    },
    {
      title: "Account Code",
      dataIndex: "gl_code",
      key: "gl_code",
      sorter: (a, b) => a.gl_code - b.gl_code,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      filters: accountTypes.map((t) => ({ text: t, value: t })),
      onFilter: (value, record) => record.type === value,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      filters: accountCategories.map((c) => ({ text: c, value: c })),
      onFilter: (value, record) => record.category === value,
    },
    {
      title: "Normal Balance",
      dataIndex: "normal_balance",
      key: "normal_balance",
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => dayjs(date).format("YYYY-MM-DD"),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, record) => (
        <Space>
          {editPermission && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              Edit
            </Button>
          )}
          {deletePermission && (
            <Popconfirm
              title="Archive this account?"
              onConfirm={() => handleArchive(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                Archive
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title="Chart of Accounts"
        extra={
          <Space>
            <RangePicker onChange={(dates) => setDateRange(dates)} />
            <Input.Search
              placeholder="Search accounts..."
              allowClear
              style={{ width: 250 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Button icon={<FileExcelOutlined />} onClick={exportToCSV}>
              CSV
            </Button>
            <Button icon={<FilePdfOutlined />} onClick={exportToPDF}>
              PDF
            </Button>
            {createPermission && (
              <>
                <Button
                  icon={<UploadOutlined />}
                  onClick={() => setImportModalVisible(true)}
                >
                  Import Accounts
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreate}
                >
                  New Account
                </Button>
              </>
            )}
            <Button icon={<ReloadOutlined />} onClick={fetchAccounts}>
              Refresh
            </Button>
          </Space>
        }
      >
        <>
          {/* Statistics Cards */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={8}>
              {/* <Card> */}
              <Statistic
                title="Total Accounts"
                value={stats.total}
                valueStyle={{ color: "#3f8800" }}
              />
              {/* </Card> */}
            </Col>
            <Col span={8}>
              {/* <Card> */}
              <Statistic
                title="Balance Sheet Accounts"
                value={stats.balanceSheetCount}
                valueStyle={{ color: "#1890ff" }}
              />
              {/* </Card> */}
            </Col>
            <Col span={8}>
              {/* <Card> */}
              <Statistic
                title="Income Statement Accounts"
                value={stats.incomeStatementCount}
                valueStyle={{ color: "#722ed1" }}
              />
              {/* </Card> */}
            </Col>
          </Row>
          <Divider />
          <Row style={{ marginBottom: 24 }}>
            {stats.topCategories.length > 0 ? (
              <>
                {stats.topCategories.map(([cat, count]) => (
                  <Col span={4}>
                    <span key={cat}>
                      {cat}: {count}
                    </span>
                  </Col>
                ))}
              </>
            ) : (
              <span>No data</span>
            )}
          </Row>
        </>
        <Divider />
        <Table
          ref={tableRef}
          columns={columns}
          dataSource={filteredAccounts}
          rowKey="id"
          loading={loading}
          size="small"
          width="100%"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          scroll={{ x: "max-content" }}
        />
      </Card>

      {/* Create/Edit Account Modal */}
      <Modal
        title={editingAccount ? "Edit Account" : "Create New Account"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleSubmit}>
            {editingAccount ? "Update" : "Create"}
          </Button>,
        ]}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="account_name"
            label="Account Name"
            rules={[{ required: true, message: "Please enter account name" }]}
          >
            <Input placeholder="e.g., Petty Cash" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Account Type"
            rules={[{ required: true, message: "Please select type" }]}
          >
            <Select placeholder="Select type">
              {accountTypes.map((type) => (
                <Option key={type} value={type}>
                  {type}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="category"
            label="Account Category"
            rules={[{ required: true, message: "Please select category" }]}
          >
            <Select placeholder="Select category" showSearch>
              {accountCategories.map((cat) => (
                <Option key={cat} value={cat}>
                  {cat}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="normal_balance"
            label="Normal Balance"
            rules={[
              { required: true, message: "Please select normal balance" },
            ]}
          >
            <Select placeholder="Debit or Credit">
              {accountNormal.map((bal) => (
                <Option key={bal} value={bal}>
                  {bal}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="gl_code"
            label="Account Code"
            rules={[{ required: true, message: "Please enter account code" }]}
          >
            <Input
              type="number"
              placeholder="e.g., 1010"
              disabled={!!editingAccount}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Import Chart of Accounts"
        open={importModalVisible}
        onCancel={() => {
          setImportModalVisible(false);
          setFileList([]);
          setParsedFile(null);
          setPreviewData({ columns: [], rows: [] });
        }}
        footer={[
          <Button key="cancel" onClick={() => setImportModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={uploading}
            onClick={handleImport}
          >
            Upload & Import
          </Button>,
        ]}
        width={800}
      >
        <div style={{ marginBottom: 16 }}>
          <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
            Download Template CSV
          </Button>
        </div>
        <Paragraph>
          Upload a CSV or Excel file containing the chart of accounts. Required
          columns:{" "}
          <strong>account_name, gl_code, type, category, normal_balance</strong>
          . You can download the template above.
        </Paragraph>
        <Dragger
          fileList={fileList}
          onChange={({ fileList }) => {
            setFileList(fileList);
            if (fileList.length > 0 && fileList[0].originFileObj) {
              parseFileForPreview(fileList[0].originFileObj);
            } else {
              setParsedFile(null);
              setPreviewData({ columns: [], rows: [] });
            }
          }}
          beforeUpload={() => false}
          maxCount={1}
          accept=".csv,.xlsx,.xls"
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag file to this area to upload
          </p>
          <p className="ant-upload-hint">
            Support for CSV, Excel (.xlsx, .xls)
          </p>
        </Dragger>

        {previewData.columns.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <Title level={5}>File Preview (first 5 rows)</Title>
            <Table
              dataSource={previewData.rows.map((row, idx) => ({
                ...row,
                key: idx,
              }))}
              columns={previewData.columns.map((col) => ({
                title: col,
                dataIndex: col,
                key: col,
                ellipsis: true,
              }))}
              pagination={false}
              size="small"
              scroll={{ x: "max-content" }}
            />
          </div>
        )}
      </Modal>

      {/* Optional: Display most used accounts section (from original) */}
      {mostUsedAccounts.length > 0 && (
        <Card title="Most Used Accounts" style={{ marginTop: 24 }}>
          <Table
            dataSource={mostUsedAccounts}
            columns={[
              { title: "Account Name", dataIndex: "account_name" },
              { title: "Usage Count", dataIndex: "count" },
            ]}
            rowKey="account_name"
            pagination={false}
          />
        </Card>
      )}
    </>
  );
};

export default ChartOfAccounts;
